// static/js/menu/MainNavigationMenu.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Box, Button, CssBaseline, Drawer, IconButton, Snackbar, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SidebarNavigation, { sidebarDimensions } from 'design/organisms/SidebarNavigation';
import VersionUpdateBanner from '@shared/components/VersionUpdateBanner';

// Icons for navigation
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import IntegrationInstructionsOutlinedIcon from '@mui/icons-material/IntegrationInstructionsOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';

// Navigation configuration with required permissions
// requiredPermissions: array of permission codes - user needs ANY of these to see the item
const allNavItems = [
  { id: 'load-schedule', label: 'Load Schedule', icon: EventNoteOutlinedIcon, href: '/schedule', requiredPermissions: ['load_view'] },
  { id: 'dashboard', label: 'Dashboard', icon: DashboardOutlinedIcon, href: '/dashboard', requiredPermissions: ['dashboard_view'] },
  { id: 'loads', label: 'Loads', icon: LocalShippingOutlinedIcon, href: '/loads', requiredPermissions: ['load_view'] },
  { id: 'drivers', label: 'Drivers', icon: PeopleOutlinedIcon, href: '/drivers', requiredPermissions: ['hr_view', 'driver_statistics'] },
  {
    id: 'fleet',
    label: 'Fleet',
    icon: DirectionsCarOutlinedIcon,
    requiredPermissions: ['fleet_view'],
    children: [
      { id: 'trucks', label: 'Trucks', href: '/trucks', requiredPermissions: ['fleet_view'] },
      { id: 'trailers', label: 'Trailers', href: '/trailers', requiredPermissions: ['fleet_view'] },
    ]
  },
  {
    id: 'customers-group',
    label: 'Customers',
    icon: BusinessOutlinedIcon,
    requiredPermissions: ['customer_view'],
    children: [
      { id: 'customers', label: 'Customers', href: '/customers', requiredPermissions: ['customer_view'] },
      { id: 'vendors', label: 'Vendors', href: '/vendors', requiredPermissions: ['customer_view'] },
      { id: 'locations', label: 'Locations', href: '/locations', requiredPermissions: ['customer_view'] },
    ]
  },
  { id: 'accounting', label: 'Accounting', icon: AccountBalanceOutlinedIcon, href: '/accounting', requiredPermissions: ['accounting_view'] },
  { id: 'safety', label: 'Safety', icon: SecurityOutlinedIcon, href: '/safety', requiredPermissions: ['safety_view'] },
  { id: 'maintenance', label: 'Maintenance', icon: BuildOutlinedIcon, href: '/maintenance', requiredPermissions: ['maintenance_view'] },
  { id: 'reports', label: 'Reports', icon: AssessmentOutlinedIcon, href: '/reports', requiredPermissions: ['reports_view'] },
  { id: 'divider1', type: 'divider' },
  { id: 'integrations-section', type: 'section', label: 'Integrations' },
  { id: 'fuel', label: 'Fuel', icon: LocalGasStationOutlinedIcon, href: '/fuel', requiredPermissions: ['integration_view'] },
  { id: 'integrations', label: 'Integrations', icon: IntegrationInstructionsOutlinedIcon, href: '/integrations', requiredPermissions: ['integration_view'] },
  { id: 'divider2', type: 'divider' },
  { id: 'ai-dispatch', label: 'AI-Dispatch', icon: SmartToyOutlinedIcon, href: '/ai-dispatch', requiredPermissions: ['ai_dispatch_view'] },
  { id: 'knowledge', label: 'Knowledge Base', icon: MenuBookOutlinedIcon, href: '/knowledge', requiredPermissions: ['knowledge_view'] },
  { id: 'tools', label: 'Tools', icon: HandymanOutlinedIcon, href: '/tools' },
  { id: 'settings', label: 'Settings', icon: SettingsOutlinedIcon, href: '/settings', requiredPermissions: ['settings_view', 'hr_users'] },
  // Owner-only infra health. The sentinel permission below is granted to no
  // role, so only superusers (who bypass permission checks) ever see it; the
  // API (IsSuperAdmin) and the page route enforce the same boundary.
  { id: 'ops-console', label: 'System Health', icon: DnsOutlinedIcon, href: '/ops', requiredPermissions: ['__superuser_only__'] },
];

/**
 * Check if user has any of the required permissions
 */
const hasPermission = (userPermissions, requiredPermissions, isSuperuser) => {
  // Superusers have all permissions
  if (isSuperuser) return true;
  // No required permissions means everyone can see it
  if (!requiredPermissions || requiredPermissions.length === 0) return true;
  // Check if user has any of the required permissions
  return requiredPermissions.some(perm => userPermissions.includes(perm));
};

/**
 * Filter nav items based on user permissions
 */
const filterNavItems = (items, userPermissions, isSuperuser) => {
  const filtered = [];
  let lastWasDivider = false;

  for (const item of items) {
    // Always include dividers and sections (we'll clean up orphans later)
    if (item.type === 'divider' || item.type === 'section') {
      // Don't add consecutive dividers
      if (item.type === 'divider' && lastWasDivider) continue;
      filtered.push(item);
      lastWasDivider = item.type === 'divider';
      continue;
    }

    // Check permission for this item
    if (!hasPermission(userPermissions, item.requiredPermissions, isSuperuser)) {
      continue;
    }

    // If item has children, filter them too
    if (item.children) {
      const filteredChildren = item.children.filter(child =>
        hasPermission(userPermissions, child.requiredPermissions, isSuperuser)
      );
      // Only include parent if it has visible children
      if (filteredChildren.length > 0) {
        filtered.push({ ...item, children: filteredChildren });
        lastWasDivider = false;
      }
    } else {
      filtered.push(item);
      lastWasDivider = false;
    }
  }

  // Clean up: remove trailing dividers and sections with no following items
  while (filtered.length > 0 && (filtered[filtered.length - 1].type === 'divider' || filtered[filtered.length - 1].type === 'section')) {
    filtered.pop();
  }

  // Clean up: remove sections followed immediately by dividers (no items in section)
  // Also skip the divider that follows the empty section
  const cleanedItems = [];
  for (let i = 0; i < filtered.length; i++) {
    const current = filtered[i];
    const next = filtered[i + 1];

    // Skip section if next item is a divider (empty section), and skip that divider too
    if (current.type === 'section' && next && next.type === 'divider') {
      i++; // skip the following divider as well
      continue;
    }
    cleanedItems.push(current);
  }

  // Remove consecutive dividers that may result from the above cleanup
  const finalItems = [];
  for (const item of cleanedItems) {
    if (item.type === 'divider' && finalItems.length > 0 && finalItems[finalItems.length - 1].type === 'divider') {
      continue;
    }
    finalItems.push(item);
  }

  return finalItems;
};

/**
 * MainNavigationMenu - Sidebar navigation wrapper
 * Provides responsive sidebar with mobile drawer support
 */
const MainNavigationMenu = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [currentPath, setCurrentPath] = useState('');
  const [userPermissions, setUserPermissions] = useState([]);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [permissionsError, setPermissionsError] = useState(false);
  const [userInfo, setUserInfo] = useState({ name: 'User', initials: 'U' });
  const isMountedRef = useRef(true);

  // Get current path and tenant slug on mount
  const tenantSlug = typeof window !== 'undefined' ? window.TENANT_SLUG || '' : '';

  // Fetch user permissions. Retries transient failures (e.g. the brief 502 gap
  // while daphne restarts during a deploy) with backoff, so a momentary blip
  // self-heals instead of silently rendering a default "User" with an empty
  // menu — which looks alarmingly like being downgraded to a different account.
  const fetchPermissions = useCallback(async () => {
    setPermissionsError(false);
    const MAX_ATTEMPTS = 5;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      try {
        const response = await fetch('/api/users/permissions/me/', {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' },
        });
        if (response.ok) {
          const data = await response.json();
          if (!isMountedRef.current) return;
          setUserPermissions(data.permissions || []);
          setIsSuperuser(data.is_superuser || false);
          setUserInfo({
            name: data.display_name || data.username || 'User',
            initials: data.initials || 'U',
          });
          setPermissionsError(false);
          setPermissionsLoaded(true);
          return;
        }
        // 401 = the session is genuinely gone; go re-authenticate rather than
        // present a stripped-down menu under a wrong-looking identity.
        if (response.status === 401) {
          window.location.href = '/login/';
          return;
        }
        // 403 / 5xx / 502 (deploy restart) — fall through and retry.
      } catch (error) {
        // Network error — fall through and retry.
      }
      // Backoff between attempts: 0.5s, 1s, 2s, 4s (covers a multi-second restart).
      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** attempt));
      }
    }
    if (isMountedRef.current) {
      console.error('Failed to fetch permissions after retries');
      setPermissionsError(true);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchPermissions();
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchPermissions]);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  // Filter nav items based on permissions. While loading OR after a failed load
  // we render no items (the error Snackbar below explains why) rather than a
  // partial menu that could be mistaken for a real, lower-privilege account.
  const navItems = (permissionsLoaded && !permissionsError)
    ? filterNavItems(allNavItems, userPermissions, isSuperuser)
    : [];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigate = (href) => {
    window.location.href = href;
  };

  // User info from API
  const user = userInfo;

  // Calculate content margin based on sidebar state
  const contentMargin = isMobile ? 0 : (collapsed ? sidebarDimensions.collapsedWidth : sidebarDimensions.width);

  return (
    <>
      <CssBaseline />

      {/* Proactive "new version available" prompt — mounted here because the
          main navigation bundle loads on every page, so it polls sitewide. */}
      <VersionUpdateBanner />

      {/* Permissions failed to load (e.g. a transient 502 while daphne restarts
          during a deploy). Make it unmistakably an error with a Retry — never a
          silent downgrade that looks like being switched to a limited account. */}
      <Snackbar
        open={permissionsError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1400 }}
      >
        <Alert
          severity="warning"
          variant="filled"
          action={
            <Button color="inherit" size="small" onClick={fetchPermissions}>
              Retry
            </Button>
          }
          sx={{ alignItems: 'center' }}
        >
          Couldn't load your menu — this is a connection issue, not a change to your account.
        </Alert>
      </Snackbar>

      {/* Desktop Sidebar */}
      {!isMobile && (
        <SidebarNavigation
          navItems={navItems}
          currentPath={currentPath}
          onNavigate={handleNavigate}
          logoText="TMS"
          user={user}
          defaultCollapsed={collapsed}
          tenantSlug={tenantSlug}
        />
      )}

      {/* Mobile App Bar */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 56,
            backgroundColor: '#0f172a',
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            zIndex: 1100,
          }}
        >
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ color: '#fff' }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ ml: 2, color: '#fff', fontWeight: 600 }}>TMS</Box>
        </Box>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: sidebarDimensions.width,
              backgroundColor: 'transparent',
              border: 'none',
            },
          }}
        >
          <SidebarNavigation
            navItems={navItems}
            currentPath={currentPath}
            onNavigate={(href) => {
              handleNavigate(href);
              setMobileOpen(false);
            }}
            logoText="TMS"
            user={user}
            tenantSlug={tenantSlug}
          />
        </Drawer>
      )}
    </>
  );
};

export default MainNavigationMenu;
